import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, Mail, Lock, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { SEO } from '../components/SEO';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email, code, done
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mockCode, setMockCode] = useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, null, { params: { email } });
      if (res.data.mock_code) {
        setMockCode(res.data.mock_code);
      }
      toast.success('Reset code sent to your email');
      setStep('code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!code) { toast.error('Please enter the reset code'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, null, {
        params: { email, code, new_password: newPassword }
      });
      toast.success('Password reset successfully!');
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="forgot-password-page">
      <SEO title="Reset Password" description="Reset your Training Solutions account password." path="/forgot-password" />
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Sign In
        </Link>

        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Briefcase className="w-6 h-6 text-white" />
          </div>
          <span className="font-outfit font-bold text-2xl text-slate-900">Training Solutions</span>
        </div>

        {step === 'email' && (
          <>
            <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-2">Forgot Password?</h2>
            <p className="text-slate-500 mb-8">Enter your email and we'll send you a reset code.</p>
            <form onSubmit={handleSendCode} className="space-y-6">
              <div>
                <Label>Email</Label>
                <div className="mt-2 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12" data-testid="forgot-email-input" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-code-btn">
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-2">Enter Reset Code</h2>
            <p className="text-slate-500 mb-2">We sent a 6-digit code to <strong>{email}</strong></p>
            {mockCode && (
              <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="mock-code-banner">
                Demo mode — Your code: <strong>{mockCode}</strong>
              </div>
            )}
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <Label>Reset Code</Label>
                <div className="mt-2 relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input type="text" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} className="pl-10 h-12 text-center text-lg tracking-widest" maxLength={6} data-testid="reset-code-input" />
                </div>
              </div>
              <div>
                <Label>New Password</Label>
                <div className="mt-2 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input type="password" placeholder="Min 6 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pl-10 h-12" data-testid="new-password-input" />
                </div>
              </div>
              <div>
                <Label>Confirm Password</Label>
                <div className="mt-2 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input type="password" placeholder="Re-enter password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-10 h-12" data-testid="confirm-password-input" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white" data-testid="reset-password-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}

        {step === 'done' && (
          <div className="text-center" data-testid="reset-success">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <KeyRound className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-2">Password Reset!</h2>
            <p className="text-slate-500 mb-8">Your password has been reset successfully.</p>
            <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 h-12" data-testid="back-to-login-btn">
              Sign In Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
